import React, { useEffect, useState } from 'react';
import { Drawer, Spin } from 'antd';
import type { TBillingApiScope, VisitDetailVo } from 'types/insuranceBilling';
import { getBillingVisitDetail } from 'api/insuranceBilling';
import { formatDateMDY } from './statusConfig';
import IssueBlock from './IssueBlock';
import FixActions, { hasActionTarget } from './FixActions';
import StatusBadge from './StatusBadge';
import s from './s.module.less';

// 还没变成 claim 的 visit 的抽屉（原型卡片 7）。每个 visit 行点开都是它 —— ready / outside
// window 也有，所以一行永远不会是死点击。
//
// 一次就诊可能同时卡在好几件事上，但抽屉只给**一个** issue block（一次就诊、一张建不出来的
// claim）。原因、文案、按修复地点归并好的建议（一处一句话、一个按钮）全部由服务端给出，
// 前端只负责排版；没有文案时不凭状态伪造原因。

// 句末补句号：服务端 headline 是「{patient}'s {date} visit」之后的半句，不带标点
const withPeriod = (text: string): string => (/[.!?]$/.test(text) ? text : `${text}.`);

// 多个问题时，原型把每句的修复地点加粗（On the **visit note** in X / In **Clients**）。
// 服务端 sentence 是纯文本，按这两种固定句式拆出地点；对不上的句子原样显示
const emphasizePlace = (sentence: string): React.ReactNode => {
    const onPlace = sentence.match(/^On the (visit note|patient's record)( in .+)$/);
    if (onPlace) {
        return (
            <>
                On the <b>{onPlace[1]}</b>
                {onPlace[2]}
            </>
        );
    }
    const inPlace = sentence.match(/^In ([^:]+)(:.*)$/);
    if (inPlace) {
        return (
            <>
                In <b>{inPlace[1]}</b>
                {inPlace[2]}
            </>
        );
    }
    return sentence;
};

type Props = {
    encounterId: number | null;
    apiScope: TBillingApiScope;
    // Provider 字段只在 Practice view 显示（原型 withProv）
    showProviderCol: boolean;
    onClose: () => void;
    // 打开时发现这次就诊已经出了 claim（出单竞态）：切到 claim 抽屉并刷新列表
    onClaimFound: (claimId: number) => void;
};

const VisitDrawer = ({ encounterId, apiScope, showProviderCol, onClose, onClaimFound }: Props) => {
    const [detail, setDetail] = useState<VisitDetailVo | null>(null);
    const [loading, setLoading] = useState(false);
    const [failed, setFailed] = useState(false);

    useEffect(() => {
        // 换目标时先清掉上一位患者的数据，失败也不能继续显示旧详情
        setDetail(null);
        setFailed(false);
        if (encounterId == null) return undefined;
        let cancelled = false;
        setLoading(true);
        (async () => {
            const res: any = await getBillingVisitDetail(encounterId, apiScope);
            if (cancelled) return;
            const vo: VisitDetailVo | null = (!res?.error && res?.data?.data) || null;
            setLoading(false);
            if (!vo) {
                setFailed(true);
                return;
            }
            if (vo.claimId != null) {
                onClaimFound(vo.claimId);
                return;
            }
            setDetail(vo);
        })();
        return () => {
            cancelled = true;
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [encounterId, apiScope]);

    // 只有「Not ready for claim」才是要 provider 修的卡住。窗口外的 visit 也带一条 issue
    // （BEFORE_START），但没什么可修，和 ready / preparing 一样给安静的状态说明
    const blocked = !!detail && detail.state?.key === 'not_ready' && detail.issues.length > 0;
    const multi = !!detail && detail.issues.length > 1;

    // 字段顺序照原型：Patient / Source EHR / Date of service / Provider（仅 Practice view）
    const fields = detail
        ? [
              { label: 'Patient', value: detail.patient },
              { label: 'Source EHR', value: detail.source || '—' },
              { label: 'Date of service', value: formatDateMDY(detail.serviceDate) },
              ...(showProviderCol ? [{ label: 'Provider', value: detail.providerName || '—' }] : []),
          ]
        : [];

    const renderBody = () => {
        if (!detail) return null;
        if (blocked) {
            const causes = multi ? (
                <ol className={s.causeList}>
                    {detail.issues.map((issue, i) => (
                        <li key={issue.code}>
                            <span className={s.causeNum}>{i + 1}</span>
                            <b>{issue.label}</b> — {issue.cause}
                        </li>
                    ))}
                </ol>
            ) : (
                <div className={s.issueText}>{detail.issues[0].cause}</div>
            );
            const sentences = detail.fixes.map((f, i) => (
                <React.Fragment key={`${f.place}-${i}`}>
                    {i > 0 && ' '}
                    {multi ? emphasizePlace(f.sentence) : f.sentence}
                </React.Fragment>
            ));
            const actions = detail.fixes.map((f) => f.action);
            const hasActions = actions.some((a) => hasActionTarget(a, detail.patientRef, detail.visitRef));
            const recommendation = (
                <>
                    {sentences}{' '}
                    {detail.onePass ? (
                        <>
                            You can do it all in one pass; Klarity picks the visit up on the next sync and creates{' '}
                            <b>one</b> claim.
                        </>
                    ) : (
                        'Klarity picks it up on the next sync.'
                    )}
                </>
            );
            return (
                <IssueBlock
                    tone="amber"
                    chip={detail.chip || "Can't be billed"}
                    issue={
                        <>
                            <b>
                                {detail.patient}&apos;s {formatDateMDY(detail.serviceDate)} visit
                            </b>
                            {detail.headline ? ` ${withPeriod(detail.headline)}` : ''}
                        </>
                    }
                    causes={causes}
                    recommendation={recommendation}
                    actions={
                        hasActions && (
                            <FixActions
                                actions={actions}
                                patient={detail.patient}
                                patientRef={detail.patientRef}
                                visitRef={detail.visitRef}
                            />
                        )
                    }
                    // visit 的修复是 provider 自己的事，他可能不知道从哪下手，所以这里保留求助入口。
                    // denied claim 没有这一行 —— 那件事 Klarity 在跟
                    support={
                        <>
                            Not sure how to fix it? Email us at{' '}
                            <a href="mailto:payer@helloklarity.com">payer@helloklarity.com</a>.
                        </>
                    }
                />
            );
        }
        // ready / preparing / outside window：没什么要修的，给一段安静的说明，免得这一行点开是空的。
        // state 为 null = 这次就诊已不在 claims 页的展示范围内
        return (
            <div className={s.issueBlockPlain}>
                {detail.state ? (
                    <>
                        <StatusBadge state={detail.state} showTip={false} />
                        {detail.state.hint && (
                            <div className={s.issueText} style={{ marginTop: 10 }}>
                                {detail.state.hint}
                            </div>
                        )}
                    </>
                ) : (
                    <div className={s.issueText}>This visit is no longer on the claims page.</div>
                )}
            </div>
        );
    };

    return (
        <Drawer
            open={encounterId != null}
            onClose={onClose}
            width={440}
            // 标题是患者姓名（原型 ibc-panel-title），徽章与表格同一组件、同样带 tooltip
            title={
                <div className={s.drawerHead}>
                    <span className={s.drawerTitle}>{detail?.patient || 'Visit'}</span>
                    {detail && <StatusBadge state={detail.state} />}
                </div>
            }
        >
            {loading && (
                <div className={s.drawerLoading}>
                    <Spin />
                </div>
            )}
            {!loading && failed && <div className={s.issueText}>We couldn&apos;t load this visit. Try again.</div>}
            {/* 原型顺序：问题块在最上面，字段在下面 */}
            {!loading && detail && (
                <div className={s.drawerStack}>
                    {renderBody()}
                    <div className={s.drawerGrid}>
                        {fields.map((f) => (
                            <div key={f.label}>
                                <div className={s.drawerLabel}>{f.label}</div>
                                <div className={s.drawerValue}>{f.value}</div>
                            </div>
                        ))}
                    </div>
                </div>
            )}
        </Drawer>
    );
};

export default VisitDrawer;
